import { filesApi } from "@dashboard/files/api";
import { productsApi } from "@dashboard/products/api";
import { fdroidsApi } from "@dashboard/repositories/api";
import { workspacesApi } from "@dashboard/workspaces/api";
import { isAnyOf, ListenerMiddlewareInstance, UnsubscribeListener } from "@reduxjs/toolkit";

import { adminApi } from ".";

// The admin tables list rows from every workspace, so the writes that change them live in the
// customer-facing slices and carry no admin tag. Tags do not cross a createApi boundary, so anything that
// adds, removes or edits one of those rows has to be listed here or the admin table keeps a stale page for
// a full cache lifetime. Keeping the tags apart is deliberate: invalidating "Products", "Files" or
// "Workspaces" from here would refetch the dashboard caches, and "Workspaces" also re-runs the workspace
// resolution in usePrefetch, on every admin write.
const productActions = isAnyOf(
    productsApi.endpoints.createProduct.matchFulfilled,
    productsApi.endpoints.deleteProduct.matchFulfilled,
    productsApi.endpoints.updateProduct.matchFulfilled,
    productsApi.endpoints.updateProductCodename.matchFulfilled,
    productsApi.endpoints.updateProductAndroidVersion.matchFulfilled,
    productsApi.endpoints.updateProductVisibility.matchFulfilled,
    productsApi.endpoints.createBuild.matchFulfilled,
    productsApi.endpoints.deleteBuild.matchFulfilled,
    productsApi.endpoints.importProductBuild.matchFulfilled,
);
const fileActions = isAnyOf(
    filesApi.endpoints.deleteFile.matchFulfilled,
    filesApi.endpoints.uploadFile.matchFulfilled,
    filesApi.endpoints.updateFile.matchFulfilled,
    filesApi.endpoints.updateFileMetadata.matchFulfilled,
    filesApi.endpoints.updateFileRollout.matchFulfilled,
);
const fdroidActions = isAnyOf(fdroidsApi.endpoints.deleteFdroidRepo.matchFulfilled);
const workspaceActions = isAnyOf(
    workspacesApi.endpoints.updateWorkspace.matchFulfilled,
    workspacesApi.endpoints.deleteWorkspace.matchFulfilled,
    workspacesApi.endpoints.updateWorkspaceMembers.matchFulfilled,
    workspacesApi.endpoints.cancelSubscription.matchFulfilled,
    workspacesApi.endpoints.claimStarterSubscription.matchFulfilled,
    workspacesApi.endpoints.redeemVoucher.matchFulfilled,
);

export const initAdminCacheListener = (listenerMiddleware: ListenerMiddlewareInstance): UnsubscribeListener => {
    return listenerMiddleware.startListening({
        matcher: isAnyOf(productActions, fileActions, fdroidActions, workspaceActions),
        effect: (action, listenerApi) => {
            const tags: ("AdminProducts" | "AdminFiles" | "FdroidRepos" | "AdminWorkspaces")[] = [];

            if (productActions(action)) {
                tags.push("AdminProducts");
            }
            if (fileActions(action)) {
                tags.push("AdminFiles");
            }
            if (fdroidActions(action)) {
                tags.push("FdroidRepos");
            }
            if (workspaceActions(action)) {
                tags.push("AdminWorkspaces");
            }
            listenerApi.dispatch(adminApi.util.invalidateTags(tags));
        },
    });
};
